import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    useMediaQuery,
    Theme,
} from '@mui/material';
import { Order } from 'models/order';
import InfiniteScroll from 'react-infinite-scroll-component';
import CertifyOrderListItem from './certify-order-item/certify-order-item.component';
import { useMutation } from '@apollo/client/react';
import { certifyUnCertifyOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import CertifyDialog from 'components/shared/certify-dialog/certify-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type CertifyOrderListProps = {
    certifyOrders: Order[];
    onFetchMore: () => void;
    handleCertifyUnCertify: (isCertify: boolean) => void;
    isDentist: boolean;
};
const CertifyOrderList: FC<CertifyOrderListProps> = ({
    certifyOrders,
    onFetchMore,
    isDentist,
    handleCertifyUnCertify,
}: CertifyOrderListProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

    const [isOpen, setIsOpen] = useState(false);
    const [isPriceSpecOpen, setIsPriceSpecOpen] = useState(false);
    const [orderIndex, setOrderIndex] = useState(-1);
    const [orders, setOrders] = useState<Order[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setOrders(certifyOrders);
    }, [certifyOrders]);

    const [certifyUnCertifyOrder, { loading }] = useMutation(certifyUnCertifyOrderTemplate, {
        onCompleted: ({ certifyUnCertifyOrder: order }) => {
            const uncertifiedOrders = JSON.parse(JSON.stringify(orders));
            const uncertifiedOrder = uncertifiedOrders.find((odr: Order) => odr._id === order._id);
            if (uncertifiedOrder) {
                uncertifiedOrder.isCertified = order.isCertified;
            }
            setOrders(uncertifiedOrders);
            handleCertifyUnCertify(order?.isCertified);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleClose = () => {
        setIsOpen(false);
        setIsPriceSpecOpen(false);
    };

    const onConfirm = async () => {
        await certifyUnCertifyOrder({ variables: { orderId: orders[orderIndex]?._id as string } });
        setIsOpen(false);
        setIsPriceSpecOpen(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, certifyOrders?.length || 0);

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={certifyOrders ? certifyOrders.length : 0}
                    next={onFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table size={!isLargeScreen ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('orders:CERTIFICATION')}</TableCell>
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell>{translate('common:PATIENT')}</TableCell>
                                <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                <TableCell>{translate('common:LAB')}</TableCell>
                                <TableCell>{translate('common:CREATED')}</TableCell>
                                <TableCell sx={{ textAlign: 'right' }}>{translate('common:PRICE')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Fragment>
                                {orders.map((order, index) => (
                                    <CertifyOrderListItem
                                        key={order._id}
                                        order={order}
                                        openPriceSpecDialog={() => {
                                            setOrderIndex(index);
                                            setIsPriceSpecOpen(true);
                                        }}
                                        openConfirmDialog={() => {
                                            setOrderIndex(index);
                                            setIsOpen(true);
                                        }}
                                        isDentist={isDentist}
                                    />
                                ))}
                            </Fragment>
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>

            <ConfirmDialog
                open={isOpen}
                handleConfirm={onConfirm}
                handleClose={handleClose}
                dialogTitle={translate('orders:REMOVE_CERTIFICATION')}
                dialogContent={translate('orders:REMOVE_CERTIFICATION_INFO')}
                confirmBtnLabel={translate('orders:REMOVE_CERTIFICATION')}
            />
            {isPriceSpecOpen && (
                <CertifyDialog
                    open={isPriceSpecOpen}
                    handleConfirm={onConfirm}
                    currency={orders[orderIndex]?.currency}
                    handleClose={handleClose}
                    showUserAndDate
                    restorations={orders[orderIndex]?.restorations || []}
                />
            )}
        </Fragment>
    );
};

export default CertifyOrderList;
